import { matchGenesysLanguage } from './genesys-lang';
import { DEFAULT_LANGUAGE, STORAGE_KEY, normalizeStoredLanguage, type LanguageCode } from './types';

/**
 * Languages on/off. Off: English everywhere, no picker, no language in the
 * Insights link. Saved choices stay in the browser either way.
 *
 * On for dev, local development, and the production deploy (`vite build --mode prod`).
 * Vite's own default mode name `production` is not that deploy and stays off.
 * Emergency: rebuild with `VITE_WIZARD_LANGUAGES=off` to force English.
 * `VITE_WIZARD_LANGUAGES=on` forces languages on for any mode.
 */
export function languagesEnabledForMode(
  mode: string | undefined,
  flag: string | null | undefined = import.meta.env.VITE_WIZARD_LANGUAGES,
): boolean {
  const override = languageSwitchOverride(flag);
  if (override === 'off') return false;
  if (override === 'on') return true;
  return mode === 'dev' || mode === 'development' || mode === 'prod';
}

function languageSwitchOverride(flag: string | null | undefined): 'on' | 'off' | null {
  const value = flag?.trim().toLowerCase();
  if (value === 'off' || value === '0' || value === 'false') return 'off';
  if (value === 'on' || value === '1' || value === 'true') return 'on';
  return null;
}

export const WIZARD_LANGUAGES_ENABLED = languagesEnabledForMode(import.meta.env.MODE);

export function readStoredLanguage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredLanguage(code: LanguageCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Private browsing or blocked storage. The choice lasts for this visit only.
  }
}

/** Language the Insights link should carry, read at click time. */
export function uiLangForLink(languagesEnabled: boolean, stored: string | null): LanguageCode | null {
  if (!languagesEnabled) return null;
  return normalizeStoredLanguage(stored);
}

export function currentUiLang(): LanguageCode | null {
  return uiLangForLink(WIZARD_LANGUAGES_ENABLED, readStoredLanguage());
}

/** Add `uiLang` only for a manual pick. Removes it otherwise. */
export function withUiLang(insightsUrl: string, uiLang: string | null): string {
  if (!insightsUrl) return insightsUrl;
  try {
    const url = new URL(insightsUrl);
    if (uiLang) url.searchParams.set('uiLang', uiLang);
    else url.searchParams.delete('uiLang');
    return url.toString();
  } catch {
    return insightsUrl;
  }
}

/**
 * Genesys `langTag` from the launch URL, or from the OAuth `state` blob after
 * the redirect (the launch query is gone then). Presence is all the caller logs.
 */
export function readLaunchLangTag(search = typeof window === 'undefined' ? '' : window.location.search): string {
  const params = new URLSearchParams(search);
  const direct = params.get('langTag');
  if (direct) return direct;
  const state = params.get('state');
  if (!state) return '';
  try {
    const restored = JSON.parse(state) as Record<string, unknown>;
    return typeof restored.langTag === 'string' ? restored.langTag : '';
  } catch {
    return '';
  }
}

/** The language a screen would choose from a tag, for the picker highlight. */
export function languageFromTag(langTag: string | null | undefined): LanguageCode {
  return matchGenesysLanguage(langTag) ?? DEFAULT_LANGUAGE;
}
